"use client"
import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Volume2, VolumeX, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideoLazyLoad } from '@/hooks/useVideoLazyLoad';

const testimonials = [
    {
        quote: "I booked my apartment at 2 AM with their AI agent. No brokers, no hassle. It was instant.",
        name: "Sarah",
        location: "Tenant",
        avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop",
        variant: "light"
    },
    {
        quote: "My AC stopped working, and the Maintenance AI had it fixed within hours. Unbelievable speed.",
        name: "Rahul",
        location: "Resident",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop",
        variant: "dark"
    },
    {
        quote: "Finally, a rental company that uses technology to actually help people, not just collect rent.",
        name: "Ahmed",
        location: "Tenant",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop",
        variant: "secondary"
    }
];

const Testimonials = () => {
    const [selectedVideo, setSelectedVideo] = useState<{ src: string; title: string } | null>(null);
    const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
    useVideoLazyLoad(fullscreenVideoRef);

    // Lock scroll when modal is open
    useEffect(() => {
        if (selectedVideo) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedVideo]);

    return (
        <section className="bg-background py-16 md:py-28 overflow-hidden relative">
            <div className="container mx-auto px-5 md:px-10 max-w-7xl">
                {/* Header */}
                <div className="flex flex-col items-center text-center mb-12 md:mb-16">
                    {/* Tag */}
                    <div className="flex items-center gap-2 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                        <span className="text-[12px] font-semibold uppercase tracking-[1.5px] text-muted-foreground">
                            Review
                        </span>
                    </div>

                    {/* Heading */}
                    <h2 className="text-[36px] md:text-[48px] lg:text-[56px] font-medium leading-[1.15] text-foreground mb-6 tracking-[-0.02em]">
                        Living in the <span className="italic">Future</span>
                    </h2>

                    {/* Description */}
                    <p className="text-muted-foreground text-[15px] md:text-[16px] leading-[1.7] max-w-[600px]">
                        Our goal is to provide clients with an exceptional real estate experience, offering top-tier service every step of the way.
                    </p>
                </div>

                {/* Testimonials Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {testimonials.map((testimonial, index) => (
                        <div
                            key={index}
                            className={`group rounded-3xl p-8 flex flex-col justify-between min-h-[320px] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${testimonial.variant === 'dark'
                                ? 'bg-primary/10 border border-primary/20'
                                : testimonial.variant === 'secondary'
                                    ? 'bg-secondary/20 border border-secondary/20'
                                    : 'bg-card border border-border hover:border-primary/20'
                                }`}
                        >
                            {/* Quote */}
                            <p className="text-[18px] md:text-[20px] font-medium leading-[1.5] mb-8 text-foreground group-hover:text-primary transition-colors duration-300">
                                &ldquo;{testimonial.quote}&rdquo;
                            </p>

                            {/* Author */}
                            <div className="flex items-center gap-4">
                                <div className="relative w-12 h-12 rounded-full overflow-hidden bg-muted group-hover:scale-110 transition-transform duration-300">
                                    <Image
                                        src={testimonial.avatar}
                                        alt={testimonial.name}
                                        title={testimonial.name}
                                        fill
                                        className="object-cover"
                                        sizes="48px"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[16px] font-medium text-foreground">
                                        {testimonial.name}
                                    </span>
                                    <span className="text-[14px] text-muted-foreground">
                                        {testimonial.location}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Video Gallery */}
                <div className="mt-24 md:mt-40">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                        <div className="max-w-xl">
                            <h3 className="text-[32px] md:text-[44px] font-medium text-foreground leading-tight tracking-tight mb-4">
                                Watch <span className="italic text-primary">Our Stories</span>
                            </h3>
                            <p className="text-muted-foreground text-[15px] md:text-[16px] leading-relaxed">
                                Deep dive into our journey, our operations, and the vibrant community we've built.
                            </p>
                        </div>
                        <div className="hidden md:flex gap-2">
                            <span className="px-4 py-2 rounded-full border border-border text-[12px] font-medium uppercase tracking-wider text-muted-foreground">Origins</span>
                            <span className="px-4 py-2 rounded-full border border-border text-[12px] font-medium uppercase tracking-wider text-muted-foreground">Operations</span>
                            <span className="px-4 py-2 rounded-full border border-border text-[12px] font-medium uppercase tracking-wider text-muted-foreground">Community</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                        {[
                            { title: "Our Journey", src: "/video/about.mp4", label: "Origins" },
                            { title: "What We Do", src: "/video/what-we-do.mp4", label: "Operations" },
                            { title: "Who We Serve", src: "/video/tenants&owner.mp4", label: "Community" }
                        ].map((video, i) => {
                            const [isMuted, setIsMuted] = useState(true);
                            const videoRef = useRef<HTMLVideoElement>(null);
                            useVideoLazyLoad(videoRef);

                            const toggleAudio = (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (videoRef.current) {
                                    videoRef.current.muted = !videoRef.current.muted;
                                    setIsMuted(videoRef.current.muted);
                                }
                            };

                            return (
                                <div
                                    key={i}
                                    onClick={() => setSelectedVideo({ src: video.src, title: video.title })}
                                    className="group relative aspect-video bg-neutral-900 rounded-[2rem] overflow-hidden shadow-2xl transition-all duration-700 hover:shadow-primary/10 cursor-pointer"
                                >
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        loop
                                        muted={isMuted}
                                        playsInline
                                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                                    >
                                        <source src={video.src} type="video/mp4" />
                                    </video>

                                    {/* Overlay Gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent opacity-40" />

                                    {/* Audio Control - Bottom Right */}
                                    <div className="absolute bottom-8 right-8 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button
                                            onClick={toggleAudio}
                                            className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 hover:scale-110 transition-all duration-300"
                                        >
                                            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                                        </button>
                                    </div>

                                    {/* Content - Bottom Left */}
                                    <div className="absolute inset-x-0 bottom-0 p-6 z-20">
                                        <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="w-1 h-1 rounded-full bg-primary" />
                                                <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">
                                                    {video.label}
                                                </span>
                                            </div>
                                            <h4 className="text-xl md:text-2xl font-medium text-white tracking-tight">
                                                {video.title}
                                            </h4>

                                            <div className="mt-3 h-0.5 w-0 group-hover:w-12 bg-primary transition-all duration-700 ease-out" />
                                        </div>
                                    </div>

                                    {/* Corner Badge */}
                                    <div className="absolute top-8 right-8 z-30">
                                        <div className="px-3 py-1 rounded-full bg-black/30 backdrop-blur-md border border-white/10 text-[10px] font-bold text-white uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            Live
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Full Screen Video Modal */}
            <AnimatePresence>
                {selectedVideo && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-2xl p-4 md:p-10"
                        onClick={() => setSelectedVideo(null)}
                    >
                        {/* Close Button */}
                        <button
                            className="absolute top-6 right-6 md:top-10 md:right-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-[110]"
                            onClick={(e) => { e.stopPropagation(); setSelectedVideo(null); }}
                        >
                            <X size={24} />
                        </button>

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-6xl aspect-video rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-white/10 bg-black"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <video
                                ref={fullscreenVideoRef}
                                autoPlay
                                controls
                                playsInline
                                className="w-full h-full object-contain"
                            >
                                <source src={selectedVideo.src} type="video/mp4" />
                            </video>

                            {/* Title Overlay (Bottom Left) */}
                            <div className="absolute bottom-10 left-10 hidden md:block pointer-events-none">
                                <h4 className="text-3xl font-medium text-white tracking-tight drop-shadow-2xl">
                                    {selectedVideo.title}
                                </h4>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Testimonials;


